import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, output, signal, untracked } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { DataCopilotApiService } from 'src/app/core/http/services/data-copilot.service';
import { AssetFlatListTableComponent } from 'src/app/shared/common-ui-components/asset-table/asset-flatlist-table.component';
import { catchError, finalize, map, of, takeUntil } from 'rxjs';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { UISkeletonTableComponent } from 'src/app/shared/ui/skeletons/table/skeleton-table.component';
import { Subject } from 'rxjs';

interface ConversationQuestion {
  id: string;
  text: string;
  conversationId: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-add-content-modal',
  standalone: true,
  imports: [
    NgClass,
    UIIconsComponent,
    ReactiveFormsModule,
    AssetFlatListTableComponent,
    UISkeletonTableComponent
  ],
  templateUrl: './add-content-modal.component.html',
  styleUrl: './add-content-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddContentModalComponent implements OnInit {
  // Inputs (developer preview)
  isOpen = input<boolean>(false);
  canvasId = input<string>('');
  isAddInsightsModal = input<boolean>(false);
  addedReportIds = input<Set<string>>(new Set<string>());
  addedQuestionIds = input<Set<string>>(new Set<string>());

  // Outputs (developer preview)
  closed = output<void>();
  addToCanvas = output<any>();

  private assetsApiService = inject(AssetsApiService);
  private dataCopilotApiService = inject(DataCopilotApiService);
  private destroy$ = new Subject<void>();

  // State signals
  listType = signal<string>('category');
  activeTab = signal<'reports' | 'conversations' | 'custom'>('reports');
  reports = signal<any[]>([]);
  conversationsSignal = signal<any[]>([]);
  expandedConversations = signal<Set<string>>(new Set());
  loadingConversationQuestions = signal<Map<string, boolean>>(new Map());
  conversationQuestionsMap = signal<Map<string, ConversationQuestion[]>>(new Map());
  loadingReports = signal<boolean>(true);
  loadingConversations = signal<boolean>(false);
  selectedAssets = signal<any[]>([]);
  selectedQuestions = signal<ConversationQuestion[]>([]);
  searchText = signal('');

  searchControl = new FormControl('');

  constructor() {
    // Re-filter selections when addedQuestionIds changes (replaces ngOnChanges)
    effect(() => {
      const addedIds = this.addedQuestionIds();
      untracked(() => {
        this.selectedQuestions.update(questions =>
          questions.filter(q => addedIds.has(q.id))
        );
        this.updateDisabledStateForQuestions();
      });
    }, { allowSignalWrites: true });

    // Re-filter selected reports when addedReportIds changes (replaces ngOnChanges)
    effect(() => {
      const addedIds = this.addedReportIds();
      untracked(() => {
        this.selectedAssets.update(assets => assets.filter(asset => addedIds.has(asset.id)));
      });
    }, { allowSignalWrites: true });
  }

  filteredConversationsList = computed(() => {
    const searchTerm = this.searchText().toLowerCase();
    const conversations = this.conversationsSignal();
    if (!searchTerm) return conversations;
    return conversations.filter(conversation =>
      (conversation.name || '').toLowerCase().includes(searchTerm)
    );
  });

  disabledReportIdsArray = computed(() => Array.from(this.addedReportIds()));

  readonly MAX_ITEMS_LIMIT = 5;

  ngOnInit(): void {
    this.loadReports();
    if (this.addedQuestionIds().size > 0) {
      this.loadConversations();
    }

    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.searchText.set(value || '');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDisabledStateForQuestions(): void {
    this.conversationQuestionsMap.update(map => {
      const newMap = new Map(map);
      newMap.forEach(questions => {
        questions.forEach((question: any) => {
          question.disabled = this.isQuestionAddedToCanvas(question.id);
        });
      });
      return newMap;
    });
  }

  getFilteredQuestions(conversationId: string): ConversationQuestion[] {
    return this.conversationQuestionsMap().get(conversationId) || [];
  }

  switchTab(tab: 'reports' | 'conversations' | 'custom'): void {
    this.activeTab.set(tab);
    if (tab === 'reports' && this.reports().length === 0) {
      this.loadReports();
    } else if (tab === 'conversations' && this.conversationsSignal().length === 0) {
      this.loadConversations();
    }
  }

  loadReports(): void {
    this.loadingReports.set(true);

    this.assetsApiService
      .getAssetByType('REPORT', 'network-only')
      .pipe(
        map((response) =>
          response.data.assetsByType.map((model: any) => ({
            id: model.id,
            name: model.name,
            description: model.description,
            tags: model.tags,
            parameters: model.parameters,
            updatedAt: model.updatedAt,
            disabled: this.isReportAddedToCanvas(model.id)
          }))
        ),
        catchError((error) => {
          console.error('Error getting saved reports:', error);
          return of([]);
        }),
        finalize(() => this.loadingReports.set(false))
      )
      .subscribe({
        next: (reports) => this.reports.set(reports),
      });
  }

  loadConversations(): void {
    this.loadingConversations.set(true);

    this.assetsApiService
      .getAssetsByRelationshipsByType(['CONVERSATION'], ['DATASCOPE'], 'no-cache')
      .pipe(
        catchError((error) => {
          console.error('Error loading conversations:', error);
          return of([]);
        }),
        finalize(() => this.loadingConversations.set(false))
      )
      .subscribe({
        next: (data: any[]) => {
          this.dataCopilotApiService.setSelectedConversationDataWithMatchingDataScope(data);
          const filtered = data.filter((conversation: any) => !conversation.parameters.smartCollaboration);
          this.conversationsSignal.set(filtered);
        }
      });

    this.dataCopilotApiService.dataCopilotConversationData$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data.conversationsData?.length > 0) {
            const filtered = data.conversationsData.filter((conversation: any) => !conversation.parameters.smartCollaboration);
            this.conversationsSignal.set(filtered);
          }
        }
      });
  }

  toggleConversation(id: string, event: Event): void {
    event.stopPropagation();

    this.expandedConversations.update(set => {
      const s = new Set(set);
      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
        this.loadConversationQuestions(id);
      }
      return s;
    });
  }

  loadConversationQuestions(conversationId: string): void {
    if (this.conversationQuestionsMap().has(conversationId)) return;

    this.loadingConversationQuestions.update(map => {
      const m = new Map(map);
      m.set(conversationId, true);
      return m;
    });

    const conversation = this.conversationsSignal().find(c => c.id === conversationId);
    if (!conversation) {
      console.error('Conversation not found:', conversationId);
      this.loadingConversationQuestions.update(map => {
        const m = new Map(map);
        m.set(conversationId, false);
        return m;
      });
      return;
    }

    this.assetsApiService.getAssetsByRelationshipsById(conversationId, ['QUESTION'], 'network-only')
      .pipe(
        catchError(error => {
          console.error(`Error loading questions for conversation ${conversationId}:`, error);
          return of([]);
        }),
        finalize(() => {
          this.loadingConversationQuestions.update(map => {
            const m = new Map(map);
            m.set(conversationId, false);
            return m;
          });
        })
      )
      .subscribe({
        next: (assets: any[]) => {
          const questions = assets[0]?.parameters?.QUESTIONS
            ?.filter((asset: any) => asset.type === 'QUESTION' && asset?.parameters?.INPUT_TEXT && asset?.parameters?.GENERATED_SQL && asset?.parameters?.SECURED_SQL)
            .map((asset: any) => ({
              id: asset.id,
              text: asset.parameters.INPUT_TEXT || asset.parameters.MESSAGE || 'No question text',
              conversationId,
              parameters: asset.parameters || {},
              disabled: this.isQuestionAddedToCanvas(asset.id)
            }));

          this.conversationQuestionsMap.update(map => {
            const m = new Map(map);
            m.set(conversationId, questions);
            return m;
          });

          if (questions) {
            questions.forEach((question: any) => {
              if (this.isQuestionAddedToCanvas(question.id)) {
                this.selectedQuestions.update(qs => {
                  if (!qs.some(q => q.id === question.id)) {
                    return [...qs, question];
                  }
                  return qs;
                });
              }
            });
          }
        }
      });
  }

  isConversationExpanded(id: string): boolean {
    return this.expandedConversations().has(id);
  }

  isQuestionLoading(conversationId: string): boolean {
    return this.loadingConversationQuestions().get(conversationId) === true;
  }

  hasQuestions(conversationId: string): boolean {
    const questions = this.conversationQuestionsMap().get(conversationId);
    return questions !== undefined && questions.length > 0;
  }

  isQuestionSelected(questionId: string): boolean {
    return this.selectedQuestions().some(q => q.id === questionId);
  }

  toggleQuestionSelection(question: ConversationQuestion, event: Event): void {
    event.stopPropagation();

    if (this.isQuestionAddedToCanvas(question.id)) return;

    const index = this.selectedQuestions().findIndex(q => q.id === question.id);

    if (index === -1) {
      if (this.isAddInsightsModal()) {
        this.selectedQuestions.set([question]);
      } else {
        this.selectedQuestions.update(qs => [...qs, question]);
      }
    } else {
      this.selectedQuestions.update(qs => qs.filter((_, i) => i !== index));
    }
  }

  public changeListType(type: string): void {
    this.listType.set(type);
  }

  handleSelectionChange(selectedAssets: any[]): void {
    selectedAssets.forEach(asset => asset.type = 'REPORT');

    if (this.isAddInsightsModal() && selectedAssets.length > 1) {
      this.selectedAssets.set([selectedAssets[selectedAssets.length - 1]]);
    } else {
      this.selectedAssets.set(selectedAssets);
    }
  }

  addSelectedToCanvas(): void {
    const payload: any = { items: [] };

    const currentSelectedAssets = this.selectedAssets();
    if (currentSelectedAssets.length > 0) {
      const reportItems = currentSelectedAssets.filter(asset => !asset?.disabled).map(report => ({
        type: 'REPORT',
        reportId: report.id,
        title: report.name,
        isAdded: this.isReportAddedToCanvas(report.id),
      }));
      payload.items.push(...reportItems);
    }

    const currentSelectedQuestions = this.selectedQuestions();
    if (currentSelectedQuestions.length > 0) {
      const questionItems = currentSelectedQuestions.filter(question => !question?.disabled).map(question => ({
        type: 'ANSWER',
        questionId: question.id,
        conversationId: question.conversationId,
        questionText: question.text,
        title: question.text,
        isAdded: this.isQuestionAddedToCanvas(question.id),
      }));
      payload.items.push(...questionItems);
    }

    if (payload.items.length > 0) {
      this.addToCanvas.emit(payload);
      setTimeout(() => this.closeModal(), 0);
    } else {
      this.closeModal();
    }
  }

  closeModal(): void {
    this.closed.emit();
  }

  isReportAddedToCanvas(reportId: string): boolean {
    return this.addedReportIds().has(reportId);
  }

  isQuestionAddedToCanvas(questionId: string): boolean {
    return this.addedQuestionIds().has(questionId);
  }

  get totalSelectedCount(): number {
    const alreadyAddedCount = this.addedReportIds().size + this.addedQuestionIds().size;
    const newlySelectedReports = this.selectedAssets().filter(asset => !this.isReportAddedToCanvas(asset.id)).length;
    const newlySelectedQuestions = this.selectedQuestions().filter(question => !this.isQuestionAddedToCanvas(question.id)).length;
    return alreadyAddedCount + newlySelectedReports + newlySelectedQuestions;
  }

  get isMaxItemsReached(): boolean {
    return this.totalSelectedCount >= this.MAX_ITEMS_LIMIT;
  }

  get isAddButtonDisabled(): boolean {
    const tab = this.activeTab();
    const hasNoNewSelection = (tab === 'reports' && this.selectedAssets().length === 0) ||
                             (tab === 'conversations' && this.selectedQuestions().length === 0);
    return hasNoNewSelection || this.totalSelectedCount > this.MAX_ITEMS_LIMIT;
  }

  onCustomCardClick(cardType: string): void {
    if (cardType === 'freeform-card') {
      if (!this.isCanvasSaved) return;

      const payload = {
        items: [{ type: 'FREEFORM_CARD', title: 'Free Form Card', cardType: 'FREEFORM_CARD' }]
      };

      this.addToCanvas.emit(payload);
      setTimeout(() => this.closeModal(), 0);
    }
  }

  get isCanvasSaved(): boolean {
    return !!(this.canvasId() && this.canvasId().trim().length > 0);
  }
}
